import type { Tone } from 'shared/components/tone-banner'

/**
 * The sound of each tone, so the worker at the bench hears the answer without looking:
 * «готово» a short rising chime, «внимание» two even beeps, «отказ» a low falling buzz.
 * Synthesised with the Web Audio API — no files to load or cache, and nothing plays
 * where there is no audio (tests, an old browser).
 */

type Note = { freq: number; at: number; length: number; wave: OscillatorType }

const NOTES: Record<Tone, readonly Note[]> = {
	ready: [
		{ freq: 880, at: 0, length: 0.09, wave: 'sine' },
		{ freq: 1320, at: 0.1, length: 0.16, wave: 'sine' },
	],
	attention: [
		{ freq: 660, at: 0, length: 0.13, wave: 'triangle' },
		{ freq: 660, at: 0.2, length: 0.13, wave: 'triangle' },
	],
	refused: [
		{ freq: 330, at: 0, length: 0.18, wave: 'sawtooth' },
		{ freq: 220, at: 0.2, length: 0.35, wave: 'sawtooth' },
	],
}

const VOLUME = 0.2

let context: AudioContext | null = null

export function playTone(tone: Tone) {
	if (typeof AudioContext === 'undefined') return
	try {
		context ??= new AudioContext()
		// Created before the page was touched, a context starts suspended; a scan is a touch.
		if (context.state === 'suspended') void context.resume()
		const start = context.currentTime + 0.01
		for (const note of NOTES[tone]) {
			const osc = context.createOscillator()
			const gain = context.createGain()
			osc.type = note.wave
			osc.frequency.value = note.freq
			// A short ramp in and out: a hard edge clicks.
			const from = start + note.at
			const to = from + note.length
			gain.gain.setValueAtTime(0, from)
			gain.gain.linearRampToValueAtTime(VOLUME, from + 0.01)
			gain.gain.setValueAtTime(VOLUME, to - 0.03)
			gain.gain.linearRampToValueAtTime(0, to)
			osc.connect(gain).connect(context.destination)
			osc.start(from)
			osc.stop(to)
		}
	} catch {
		// A sound is a courtesy: the banner says the same.
	}
}
